import { createSelector } from "reselect";
import { Map } from "immutable";
import _ from "lodash";

import { AuthNormalisedEntities } from "@auth/models";
import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
    getEntities as getAuthEntities,
} from "@auth/selectors";
import { FilesNormalisedEntities } from "@files/models";
import { getEntities as getFileEntities, makeHydrateImage } from "@files/selectors";
import { formatDuration } from "@util/index";
import { getInitialState } from "../reducers";
import {
    MusicState as State,
    Album,
    IAlbum,
    Track,
    ITrack,
    Artist,
    ArtistPivot,
    MusicNormalisedEntities,
    IArtist,
} from "../models";

export interface PropsWithId {
    params: {
        id: string;
    };
}

export interface PropsWithTracks {
    tracks: Track[];
}

export interface PropsWithAlbums {
    albums: Album[];
}

export const getEntities = (state: State) =>
    state.music.entities ? state.music.entities : getInitialState().entities;
export const getTrackMapping = (state: State) =>
    state.music.trackMapping ? state.music.trackMapping : getInitialState().trackMapping;
export const getListAlbums = (state: State) => state.music.lists.albums;

// Get the track mapping for a track
const getPropId =  (_state: State, { params: { id } }: PropsWithId) => Number(id);
export const makeGetMappingForTrack = () => createSelector(
    getTrackMapping,
    getPropId,
    (trackMapping: Map<number, number>, id: number) => trackMapping.has(id) ? trackMapping.get(id) : null,
);

export const getAlbums = createSelector(
    getEntities,
    (entities: MusicNormalisedEntities) => entities.albums,
);

export const getTracks = createSelector(
    getEntities,
    (entities: MusicNormalisedEntities) => entities.tracks,
);

export const getArtists = createSelector(
    getEntities,
    (entities: MusicNormalisedEntities) => entities.artists,
);

// Get individual entities
const getAlbum = ({ music }: State, props: PropsWithId) => {
    const id = Number(props.params.id);
    if (music.entities && music.entities.albums.has(id)) {
        const album = music.entities.albums.get(id);
        if (album.exists) {
            return album;
        }
        else {
            return null;
        }
    }
    return new Album({ id });
};
const getTrack = ({ music }: State, props: PropsWithId) => {
    const id = Number(props.params.id);
    if (music.entities && music.entities.tracks.has(id)) {
        const track = music.entities.tracks.get(id);
        if (track.exists) {
            return track;
        }
        else {
            return null;
        }
    }
    return new Track({ id });
};
const getArtist = ({ music }: State, props: PropsWithId) => {
    const id = Number(props.params.id);
    if (music.entities && music.entities.artists.has(id)) {
        const artist = music.entities.artists.get(id);
        if (artist.exists) {
            return artist;
        }
        else {
            return null;
        }
    }
    return new Artist({ id });
};

export const makeGetAlbum = () => createSelector(
    getAlbum,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        item: Album,
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateAlbum(entities, files, auth);
        return hydrate(item) as Album;
    },
);

export const makeGetTrack = () => createSelector(
    getTrack,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        item: Track,
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateTrack(entities, files, auth);
        return hydrate(item) as Track;
    },
);

export const makeGetArtist = () => createSelector(
    getArtist,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        item: Artist,
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateArtist(entities, files, auth);
        return hydrate(item) as Artist;
    },
);

// Get array of entities
const getTracksArray = (state: State, { tracks }: PropsWithTracks) => {
    return _.map(tracks, (track) => getTrack(state, {params: {id: String(track.id)}}));
};
const getAlbumsArray = (state: State, { albums }: PropsWithAlbums) => {
    return _.map(albums, (album) => getAlbum(state, {params: {id: String(album.id)}}));
};

export const makeGetTracks = () => createSelector(
    getTracksArray,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        items: Track[],
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateTrack(entities, files, auth);
        return _.map(items, (item) => hydrate(item) as Track);
    },
);

export const makeGetAlbums = () => createSelector(
    getAlbumsArray,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        items: Album[],
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateAlbum(entities, files, auth);
        return _.map(items, (item) => hydrate(item) as Album);
    },
);

// Get array of entities
export const getAlbumsList = createSelector(
    getListAlbums,
    getEntities,
    getFileEntities,
    getAuthEntities,
    (
        list: number[],
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const { albums } = entities;
        const hydrate = makeHydrateAlbum(entities, files, auth);
        // Only return existing hydrated albums.
        return _.reduce(list, (result, id) => {
                if (albums.has(id)) {
                    result.push(albums.get(id));
                }
                return result;
            }, [] as Album[])
            .map((item) => hydrate(item) as Album);
    },
);

export const getLatestAlbums = createSelector(
    getAlbumsList,
    (albums) => albums.slice(0, 20),
);

// Functions to hydrate the models.
const hydrateArtists = (entities: MusicNormalisedEntities) => (e: Track | Album) => {
    if (e == null) {
        return e;
    }

    let matches: ArtistPivotsById = {};
    if (entities.hasOwnProperty("artistPivots")) {
        matches = getArtistPivotsByArtistId(entities.artistPivots, e);
    }

    let arr: Array<number | Artist> = [];
    const artistNames: string[] = [];
    if (e.artists != null) {
        for (let i = 0; i < e.artists.length; i++) {
            let artist = e.artists[i];
            if (typeof artist === "number") {
                artist = entities.artists.get(artist);
            }

            if (artist != null) {
                // Populate the pivot
                if (matches.hasOwnProperty(artist.id) && matches[artist.id].length > 0) {
                    const pivot = matches[artist.id].shift();
                    artist = artist.set("pivot", pivot) as Artist;

                    // Update the artist names array
                    if (artist.pivot.isMain) {
                        artistNames.push(artist.name);
                    }
                }
            }

            arr[i] = artist;
        }

        // Sort the artists by the pivot order.
        // Also filter out non-main/featured artists
        arr = _.sortBy(arr, ["pivot.order"])
            .filter(
                (a: Artist) =>
                    a != null
                    && (a.pivot == null || a.pivot?.isMain || a.pivot?.roleName === "featuring"));

        e = e.setCast("artists", arr);
        e = e.setCast("artistsStr", artistNames.join(", "));
    }

    return e;
};

interface ArtistPivotsById {
    [key: number]: ArtistPivot[];
}

const getArtistPivotsByArtistId = (artistPivots: Map<string, ArtistPivot>, e: Track | Album) => {
    // Get the key to search for pivots.
    let keyPrefix = "";
    if (e instanceof Track) {
        keyPrefix = "track_" + e.id + "_";
    }
    else {
        keyPrefix = "album_" + e.id + "_";
    }

    // Then find all artistPivots that match the key.
    const keys = artistPivots.keySeq().toArray();
    const matches: ArtistPivotsById = {};
    for (const key of keys) {
        if (key.startsWith(keyPrefix)) {
            // Get the artist ID.
            const artistId = Number(key.substring(
                keyPrefix.length,
                key.indexOf("_", keyPrefix.length),
            ));
            // And add it to the matches map
            if (!matches.hasOwnProperty(artistId)) {
                matches[artistId] = [];
            }
            matches[artistId].push(artistPivots.get(key));
        }
    }

    return matches;
};

export const makeHydrateAlbum =
    (
        entities: MusicNormalisedEntities,
        files: FilesNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) =>
        (e: Album | number, trackId?: number, depth: number = 0): Album => {
            // if it's an ID, get it from the state.
            if (typeof e === "number") {
                e = entities.albums.get(e);
            }

            // Hydrate the artists
            e = hydrateArtists(entities)(e) as Album;

            if (e == null) {
                // Nothing to hydrate
                return e as Album;
            }

            // Hydrate the tracks
            const hydrateTrack = makeHydrateTrack(entities, files, auth);
            if (e.tracks != null && e.tracks.length > 0 && depth < 1) {
                const tracks = new Array(e.tracks.length);
                for (let i = 0; i < e.tracks.length; i++) {
                    tracks[i] = hydrateTrack(e.tracks[i], e.id, ++depth) as Track;
                }
                e = e.set("tracks", tracks) as Album;
            }
            else {
                e = e.set("tracks", undefined) as Album;
            }
            // The artwork
            const album = {} as IAlbum;
            if (e.artworkId != null && files.images.has(e.artworkId)) {
                album.artwork = makeHydrateImage(files)(files.images.get(e.artworkId));
            }
            // The format
            if (e.formatName != null && entities.albumFormats.has(e.formatName)) {
                album.format = entities.albumFormats.get(e.formatName);
            }
            // The status
            if (e.statusName != null && entities.albumStatuses.has(e.statusName)) {
                album.status = entities.albumStatuses.get(e.statusName);
            }
            // The genre
            if (e.genreId != null && entities.genres.has(e.genreId)) {
                album.genre = entities.genres.get(e.genreId);
            }
            // The additional genre
            if (e.additionalGenreId != null && entities.genres.has(e.additionalGenreId)) {
                album.additionalGenre = entities.genres.get(e.additionalGenreId);
            }
            // The label
            if (e.labelId != null && entities.labels != null && entities.labels.has(e.labelId)) {
                album.label = entities.labels.get(e.labelId);
            }
            // The organisation
            if (e.organisationId != null && auth.organisations.has(e.organisationId)) {
                album.organisation = auth.organisations.get(e.organisationId);
            }
            // The pivot
            if (trackId != null) {
                album.pivot = entities.albumTracks.get(e.id + "_" + trackId);
            }

            // And the computed data
            e = e.setViewProperties();
            e = e.setCast("hydrated", true);

            return e.merge(album) as Album;
        };

export const makeHydrateTrack = (
    entities: MusicNormalisedEntities,
    files: FilesNormalisedEntities,
    auth: AuthNormalisedEntities,
) =>
    (e: Track | number, albumId?: number, depth: number = 0): Track => {
        // if it's an ID, get it from the state.
        if (typeof e === "number") {
            e = entities.tracks.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as Track;
        }

        // Hydrate the albums
        const hydrateAlbum = makeHydrateAlbum(entities, files, auth);
        if (e.albums != null && e.albums.length > 0 && depth < 1) {
            const albums = new Array(e.albums.length);
            for (let i = 0; i < e.albums.length; i++) {
                albums[i] = hydrateAlbum(e.albums[i], e.id, ++depth) as Album;
            }
            e = e.set("albums", albums) as Track;
        }
        else {
            e = e.set("albums", undefined) as Track;
        }
        // Hydrate the artists
        e = hydrateArtists(entities)(e) as Track;
        // Hydrate the pivot
        const track = {} as ITrack;
        if (albumId != null) {
            track.pivot = entities.albumTracks.get(albumId + "_" + e.id);
        }

        // And the computed data
        e = e.setCast("durationFormatted", formatDuration(e.duration));
        e = e.setCast("viewUrl", e.getViewUrl());
        e = e.setCast("viewText", e.getTitle());
        e = e.setCast("hydrated", true);

        return e.merge(track) as Track;
    };

export const makeHydrateArtist = (
    entities: MusicNormalisedEntities,
    files: FilesNormalisedEntities,
    auth: AuthNormalisedEntities,
) =>
    (e: Artist | number, depth: number = 0): Artist => {
        // if it's an ID, get it from the state.
        if (typeof e === "number") {
            e = entities.artists.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as Artist;
        }

        // Hydrate the albums
        const hydrateAlbum = makeHydrateAlbum(entities, files, auth);
        if (e.albums != null && e.albums.length > 0 && depth < 1) {
            const albums = new Array(e.albums.length);
            for (let i = 0; i < e.albums.length; i++) {
                albums[i] = hydrateAlbum(e.albums[i], e.id, ++depth) as Album;
            }
            e = e.set("albums", albums) as Artist;
        }
        else {
            e = e.set("albums", undefined) as Artist;
        }
        const artist = {} as IArtist;

        // And the computed data
        e = e.setCast("viewUrl", e.getViewUrl());
        e = e.setCast("viewText", e.getName());
        e = e.setCast("hydrated", true);

        return e.merge(artist) as Artist;
    };

// Permissions selectors
const albumPermissionPrefix = "wata.music.album";
export const canViewAlbum = canPermissionSelectorFactory(albumPermissionPrefix, "view");
export const canCreateAlbum = canPermissionSelectorFactory(albumPermissionPrefix, "create");
export const canUpdateAlbum = canPermissionSelectorFactory(albumPermissionPrefix, "update");
export const canDeleteAlbum = canPermissionSelectorFactory(albumPermissionPrefix, "delete");
export const getViewAlbumLevel = levelPermissionSelectorFactory(albumPermissionPrefix, "view");
export const getCreateAlbumLevel = levelPermissionSelectorFactory(albumPermissionPrefix, "create");
export const getUpdateAlbumLevel = levelPermissionSelectorFactory(albumPermissionPrefix, "update");
export const getDeleteAlbumLevel = levelPermissionSelectorFactory(albumPermissionPrefix, "delete");

const trackPermissionPrefix = "wata.music.track";
export const canViewTrack = canPermissionSelectorFactory(trackPermissionPrefix, "view");
export const canCreateTrack = canPermissionSelectorFactory(trackPermissionPrefix, "create");
export const canUpdateTrack = canPermissionSelectorFactory(trackPermissionPrefix, "update");
export const canDeleteTrack = canPermissionSelectorFactory(trackPermissionPrefix, "delete");
export const getViewTrackLevel = levelPermissionSelectorFactory(trackPermissionPrefix, "view");
export const getCreateTrackLevel = levelPermissionSelectorFactory(trackPermissionPrefix, "create");
export const getUpdateTrackLevel = levelPermissionSelectorFactory(trackPermissionPrefix, "update");
export const getDeleteTrackLevel = levelPermissionSelectorFactory(trackPermissionPrefix, "delete");

const artistPermissionPrefix = "wata.music.artist";
export const canViewArtist = canPermissionSelectorFactory(artistPermissionPrefix, "view");
export const canCreateArtist = canPermissionSelectorFactory(artistPermissionPrefix, "create");
export const canUpdateArtist = canPermissionSelectorFactory(artistPermissionPrefix, "update");
export const canDeleteArtist = canPermissionSelectorFactory(artistPermissionPrefix, "delete");
export const getViewArtistLevel = levelPermissionSelectorFactory(artistPermissionPrefix, "view");
export const getCreateArtistLevel = levelPermissionSelectorFactory(artistPermissionPrefix, "create");
export const getUpdateArtistLevel = levelPermissionSelectorFactory(artistPermissionPrefix, "update");
export const getDeleteArtistLevel = levelPermissionSelectorFactory(artistPermissionPrefix, "delete");
